import { useEffect } from 'react';
import Auth, { CognitoUser } from '@aws-amplify/auth';
import { ClientJS } from 'clientjs';
import { useAppContext } from '@/context/app';
import { useNavigate } from 'react-router-dom';
import { AuthUser } from '@/types/Auth.ts';
import useApi from '@/hooks/useApi.ts';
import { AdminRole } from '@/types/AppRole.ts';
import { useCallback } from 'react';
import { AppUserUpdateUp } from '@/types/AppUser.ts';
import useControlPanel from '@/hooks/useControlPanel';
import { UserState } from '@/types/ControlPanel.ts';
import { AuthUtils } from '@/utils/AuthUtils';

type UseAuthHook = {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  forgot: (email: string) => Promise<void>;
  setUserAttributes: (params: Pick<AppUserUpdateUp, 'name'>) => Promise<void>;
  forgotSubmit: (code: string, password: string, email?: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  logOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  loadRoles: () => Promise<void>;
  revokeOtherSessions: (email?: string) => Promise<void>;
};

const useAuth = (): UseAuthHook => {
  const navigate = useNavigate();
  const { auth, setAuthUser, cleanState, setAuthTempInfo, setUserRoles } = useAppContext();
  const { apiGet, apiPost } = useApi();
  const { updateUserState } = useControlPanel();

  const {
    auth: { user },
    controlPanelUser,
  } = useAppContext();

  useEffect(() => {
    if (user && controlPanelUser.state === UserState.INACTIVE) {
      updateUserState(user, UserState.ACTIVE);
    }
  }, [user]);

  const token = auth?.user?.token;
  const login = (email: string, password: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const client = new ClientJS();

      const fingerprint = client.getFingerprint().toString();
      const userAgent = navigator.userAgent;
      const screenResolution = `${window.screen.width}x${window.screen.height}`;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      Auth.signIn(email, password, {
        userAgent,
        screenResolution,
        timezone,
        fingerprint,
      })
        .then(async value => {
          const user = value as CognitoUser;
          const session = user.getSignInUserSession();
          if (session) {
            const newUser = {
              token: session.getIdToken().getJwtToken(),
              email: session.getIdToken().decodePayload()['email'] as string,
              id: session.getIdToken().decodePayload()['sub'] as string,
              name: session.getIdToken().decodePayload()['name'] as string,
              //roles: JSON.parse(session.getIdToken().decodePayload()['roles'] ?? "[]")
            };
            setAuthUser(newUser);
            // updateUserState(newUser, UserState.ACTIVE);
            resolve();
          } else {
            reject(`No session Associated!!`);
          }
          resolve();
        })
        .catch(err => {
          cleanState();
          reject(err);
        });
    });
  };

  const logOut = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      Auth.signOut()
        .then(() => {
          updateUserState(user, UserState.INACTIVE);
          cleanState();
          navigate('/auth/login');
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  };

  const forgot = (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      Auth.forgotPassword(email)
        .then(() => {
          setAuthTempInfo({ email });
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  };

  const forgotSubmit = (code: string, password: string, email?: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      email = email ? email : auth?.tempInfo?.email;
      Auth.forgotPasswordSubmit(email as string, code, password)
        .then(() => {
          cleanState();
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  };

  const setUserAttributes = (params: Pick<AppUserUpdateUp, 'name'>): Promise<void> => {
    return new Promise((resolve, reject) => {
      Auth.currentAuthenticatedUser()
        .then(async user => {
          try {
            await Auth.updateUserAttributes(user, params);
            resolve();
          } catch (err) {
            reject(err);
          }
        })
        .catch(err => reject(err));
    });
  };

  const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
    try {
      const user: CognitoUser = await Auth.currentAuthenticatedUser();

      await Auth.changePassword(user, oldPassword, newPassword);

      await revokeOtherSessions();

      await login(user.getUsername(), newPassword);
    } catch (error) {
      AuthUtils.handleUnauthorizedCognitoError(error);

      return Promise.reject(error);
    }
  };

  const checkAuth = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      Auth.currentSession()
        .then(async session => {
          if (session) {
            try {
              // const info = await Auth.currentUserInfo()
              // const rolesAttr = Array.from<string>(JSON.parse(info?.attributes["custom:roles"] ?? "[]"))

              setAuthUser({
                token: session.getIdToken().getJwtToken(),
                email: session.getIdToken().decodePayload()['email'] as string,
                id: session.getIdToken().decodePayload()['sub'] as string,
                name: session.getIdToken().decodePayload()['name'] as string,
              });
              resolve();
            } catch (err) {
              reject(err);
            }
          } else {
            reject(`No session Associated!!`);
          }
        })
        .catch(err => {
          cleanState();
          reject(err);
        });
    });
  };

  // revoke other sessions for custom email or current user
  const revokeOtherSessions = async (email?: string): Promise<void> => {
    try {
      await apiPost('/admin/users/sessions/revoke-all', { email });
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const loadRoles = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      Auth.currentUserInfo()
        .then(async info => {
          if (info) {
            try {
              const rolesAttr = Array.from<string>(
                JSON.parse(info?.attributes['custom:roles'] ?? '[]'),
              );

              if (rolesAttr.length > 0) {
                const roles = await apiGet<Array<AdminRole>>(
                  `/admin/roles/ids/${rolesAttr.join(',')}`,
                );
                setUserRoles(roles);
              }
              resolve();
            } catch (err) {
              reject(err);
            }
          } else {
            reject(`No User info Associated!!`);
          }
        })
        .catch(err => {
          cleanState();
          reject(err);
        });
    });
  }, [token]);

  return {
    user: auth.user,
    login,
    logOut,
    forgot,
    forgotSubmit,
    setUserAttributes,
    changePassword,
    checkAuth,
    loadRoles,
    revokeOtherSessions,
  };
};

export default useAuth;
